import { Injectable, Type } from '@angular/core';
import { AbstractService } from './abstract-service';
import { SolType } from '../models/core/sol-type';
import { SolViewId } from '../models/core/sol-view-id';
import { SolViewOptions } from '../models/core/sol-view-options';
import { ChunkTools } from '../tools/chunk-tools';

export interface ViewState {
  initialized: boolean;
  types: SolType<any>[];
  registrations: ViewRegistration<any>[];
  views: Map<string, ViewRegistration<any>>;
}

export interface ViewRegistration<O> {
  type: SolType<any>;
  viewType: SolViewType;
  viewId: SolViewId<O>;
  loadComponent: () => Promise<Type<any>>;
  componentType: () => Type<any>;
  isDefault: boolean;
  viewOptions?: Partial<O>;
  mapValue?: (value: any) => any;
  mapValueOut?: (value: any) => any;
}

export type SolViewType = 'read' | 'edit';

/**
 * Optional configuration for {@link ViewService.deriveView}.
 *
 * Generic over `S` (source/reused component value type), `T` (target/stored value type) and
 * `O` (target view options):
 * - `sourceViewId` picks a specific source view to reuse (defaults to the source type's default view).
 * - `viewOptions` are baked into the derived registration and merged under any caller-supplied options.
 * - `mapValue` maps the stored target value into the reused component's value (read direction).
 * - `mapValueOut` maps the component's emitted value back into the stored target value (write direction);
 *   provide it for editable derivations so the value round-trips into the target type.
 */
export interface ViewDerivation<S, T, O extends SolViewOptions> {
  isDefault?: boolean;
  sourceViewId?: SolViewId<any>;
  viewOptions?: Partial<O>;
  mapValue?: (value: T) => S;
  mapValueOut?: (value: S) => T;
}

interface ViewRegistrationConfig<S, T, O extends SolViewOptions> {
  isDefault?: boolean;
  viewOptions?: Partial<O>;
  mapValue?: (value: T) => S;
  mapValueOut?: (value: S) => T;
}

@Injectable({
  providedIn: 'root',
})
export class ViewService extends AbstractService<ViewState> {
  // Cache
  private views: Map<string, Type<any>> = new Map<string, Type<any>>();

  constructor() {
    super({
      initialized: false,
      types: [],
      registrations: [],
      views: new Map<string, ViewRegistration<any>>(),
    });
  }

  public async initialize(): Promise<void> {
    this.patch({ initialized: true });

    const loadPromises = this.state.registrations.map(async registration => {
      const componentType = await ChunkTools.load(() => registration.loadComponent());
      const key = registration.type.id + '-' + registration.viewType + '-' + registration.viewId.id;
      this.views.set(key, componentType);
    });

    await Promise.all(loadPromises);
  }

  public addView<T, O extends SolViewOptions>(
    type: SolType<T>,
    viewType: SolViewType,
    viewId: SolViewId<O>,
    loadComponent: () => Promise<Type<any>>,
    isDefault: boolean = false,
    mapValue?: (value: T) => T,
  ) {
    this.doAddView<T, T, O>(type, viewType, viewId, loadComponent, { isDefault, mapValue });
  }

  /**
   * Derives a new view registration from an existing one. Reuses the source view's
   * component but registers it for a different type/viewId. See {@link ViewDerivation}
   * for the optional source view, baked-in view options and read/write value maps.
   */
  public deriveView<S, T, O extends SolViewOptions>(
    sourceType: SolType<S>,
    sourceViewType: SolViewType,
    targetType: SolType<T>,
    targetViewId: SolViewId<O>,
    derivation: ViewDerivation<S, T, O> = {},
  ) {
    const source = this.getView(sourceType, sourceViewType, derivation.sourceViewId);
    if (!source) {
      const viewIdSuffix = derivation.sourceViewId ? `-${derivation.sourceViewId.id}` : '';
      throw new Error(`Cannot derive view: no registration for ${sourceType.id}-${sourceViewType}${viewIdSuffix}`);
    }
    this.doAddView<S, T, O>(targetType, sourceViewType, targetViewId, source.loadComponent, {
      isDefault: derivation.isDefault,
      viewOptions: derivation.viewOptions,
      mapValue: derivation.mapValue,
      mapValueOut: derivation.mapValueOut,
    });
  }

  private doAddView<S, T, O extends SolViewOptions>(
    type: SolType<T>,
    viewType: SolViewType,
    viewId: SolViewId<O>,
    loadComponent: () => Promise<Type<any>>,
    config: ViewRegistrationConfig<S, T, O> = {},
  ) {
    if (this.state.initialized) {
      throw new Error('Cannot register view after initialization');
    }
    const componentType = () => {
      return this.views.get(type.id + '-' + viewType + '-' + viewId.id)!;
    };
    const registration: ViewRegistration<any> = {
      type,
      viewType,
      viewId,
      loadComponent,
      componentType: componentType,
      isDefault: config.isDefault ?? false,
      viewOptions: config.viewOptions,
      mapValue: config.mapValue,
      mapValueOut: config.mapValueOut,
    };
    this.update(state => {
      const types = state.types.includes(type) ? state.types : [...state.types, type];
      const registrations = [...state.registrations, registration];
      const views = ViewService.maybeUpdateMap(state.views, registration);
      return { types, registrations, views };
    });
  }

  /**
   * Returns the most appropriate view registration to display the requested:
   * - type (e.g. string, number, boolean, etc)
   * - viewType (e.g. edit / read)
   * - viewId (e.g. textField, checkbox, multiSelect, etc)
   *
   * When no viewId is specified, the default view of the given type is returned.
   */
  public getView<T, O>(
    type: SolType<T>,
    viewType: SolViewType,
    viewId?: SolViewId<O>,
  ): ViewRegistration<O> | undefined {
    const map = this.state.views;

    // Try this type and then walk up the parent chain
    let current: SolType<any> | undefined = type;
    while (current) {
      const key = `${current.id}-${viewType}` + (viewId ? '-' + viewId.id : '');
      const registration = map.get(key) ?? undefined;
      if (registration !== undefined) {
        return registration;
      }

      // Fallback to default view (no viewId) for this type
      if (viewId) {
        const defaultKey = `${current.id}-${viewType}`;
        const defaultRegistration = map.get(defaultKey) ?? undefined;
        if (defaultRegistration !== undefined) {
          return defaultRegistration;
        }
      }

      current = current.parent;
    }

    return undefined;
  }

  public exists(type: SolType<any>, viewType: SolViewType, viewId?: SolViewId<any>): boolean {
    return !!this.getView(type, viewType, viewId);
  }

  /**
   * Looks up a registered `SolViewId` by its string id (e.g. 'textArea').
   *
   * Used when a backend payload references a view by id (`SolFieldDescriptor.viewId`)
   * and the FE needs to resolve it to the actual `SolViewId` instance to hand to
   * `SolFormFieldDef.of`. Throws when no view is registered with that id — callers
   * must surface that as a payload error rather than silently fall back to a
   * default view.
   */
  public resolveViewId(id: string): SolViewId<any> {
    const registration = this.state.registrations.find(r => r.viewId.id === id);
    if (!registration) {
      throw new Error(`No registered SolViewId with id '${id}'.`);
    }
    return registration.viewId;
  }

  private static maybeUpdateMap(
    map: Map<string, ViewRegistration<any>>,
    registration: ViewRegistration<any>,
  ): Map<string, ViewRegistration<any>> {
    // Register on type, viewType and viewId
    const updatedMap = new Map<string, ViewRegistration<any>>(map);
    const keyViewId = registration.type.id + '-' + registration.viewType + '-' + registration.viewId.id;
    ViewService.setView(updatedMap, keyViewId, registration, false);

    // Register on type and viewType
    if (registration.isDefault) {
      const keyViewType = registration.type.id + '-' + registration.viewType;
      ViewService.setView(updatedMap, keyViewType, registration, true);
    }
    return updatedMap;
  }

  private static setView(
    map: Map<string, ViewRegistration<any>>,
    key: string,
    registration: ViewRegistration<any>,
    canOverwrite: boolean,
  ): void {
    if (map.has(key) && !canOverwrite) {
      throw new Error(`Duplicate view registration: ${key}`);
    }
    map.set(key, registration);
  }
}
